import { useMemo } from 'react';

import dayjs from 'dayjs';
import { Link as RouterLink } from 'react-router-dom';
import {
  Button,
  Icons,
  LayoutCard,
  NoData,
  Box,
  mergeSx,
} from '@cast/design-system';
import {
  applyOperationalStateToResourceMetric,
  mapCpuGaugeToResourceMetric,
  mapGpuGaugeToResourceMetric,
  mapMemoryGaugeToResourceMetric,
  mapStorageGaugeToResourceMetric,
  useClusterDashboardState,
  type ResourceMetricView,
} from '@cast/features-cluster-shared';
import { EMPTY_METRIC_VALUE } from '@cast/features-cluster-shared-utils';
import { useOnboardClusterDialog } from '@cast/split-me/hooks/dialogs';
import { useChartsData } from '@cast/split-me/features/cluster/dashboard/GaugeChartsSection';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import { useReportCapabilitiesQuery } from '@cast/shared-cost-data-access';

import { buildClusterInventoryWorkloadsGpuUtilizationPath } from '@oneclick/constants/navigation';
import { clusterDashboardResourceColors } from '../_constants/dashboardTokens';
import { DashboardSectionError } from './shared/DashboardSectionError';
import { ResourcePanel } from './shared/ResourcePanel';
import { ResourcePanelSkeleton } from './shared/ResourcePanelSkeleton';

const resourceColors = clusterDashboardResourceColors;

const minResourceCardWidth = 240;

const createLockedResourcePlaceholder = (
  title: string
): ResourceMetricView => ({
  title,
  provisioned: EMPTY_METRIC_VALUE,
  unit: '',
  segments: [],
  subMetrics: [],
});

export const ResourcesSection = () => {
  const { cluster } = useCluster();
  const { showStorage, showOperationalMetrics, showAgentInstallBanner } =
    useClusterDashboardState();
  const { openOnboardClusterDialog } = useOnboardClusterDialog();
  const { cpu, memory, gpu, storage, isLoading, hasError, refetch } =
    useChartsData();

  const startOfCurrentHour = dayjs().startOf('hour');
  const { data: reportCapabilities } = useReportCapabilitiesQuery({
    clusterId: cluster.id,
    startTime: startOfCurrentHour.subtract(1, 'hour').toISOString(),
    endTime: startOfCurrentHour.endOf('hour').toISOString(),
    enabled: !!cluster.id,
  });
  const showGpu = reportCapabilities?.gpuDataAvailable;

  const resources = useMemo(() => {
    const applyState = (
      metric: ReturnType<typeof mapCpuGaugeToResourceMetric>
    ) => applyOperationalStateToResourceMetric(metric, showOperationalMetrics);

    const cpuMetric = applyState(
      mapCpuGaugeToResourceMetric(cpu, resourceColors)
    );
    const memoryMetric = applyState(
      mapMemoryGaugeToResourceMetric(memory, resourceColors)
    );
    const gpuMetric = applyState(
      mapGpuGaugeToResourceMetric(gpu, resourceColors)
    );
    const storageMetric = showStorage
      ? applyState(mapStorageGaugeToResourceMetric(storage, resourceColors))
      : undefined;

    return {
      cpu: cpuMetric,
      // Agent-missing: Memory/Storage stay locked even when summary data exists.
      memory: showAgentInstallBanner
        ? memoryMetric ?? createLockedResourcePlaceholder('Memory')
        : memoryMetric,
      gpu: showGpu ? gpuMetric : undefined,
      storage: showAgentInstallBanner
        ? showStorage
          ? storageMetric ?? createLockedResourcePlaceholder('Storage')
          : undefined
        : storageMetric,
    };
  }, [
    cpu,
    gpu,
    memory,
    showAgentInstallBanner,
    showGpu,
    showOperationalMetrics,
    showStorage,
    storage,
  ]);

  const panelCount = Object.values(resources).filter(Boolean).length;
  const hasResourcePanels =
    !!resources.cpu ||
    !!resources.memory ||
    !!resources.gpu ||
    !!resources.storage;
  const showEmptyState = !isLoading && !hasResourcePanels;

  return (
    <LayoutCard
      title="Resources"
      testId="resources-section"
      sx={{
        containerType: 'inline-size',
      }}
    >
      <DashboardSectionError hasError={hasError} onRetry={refetch}>
        {showEmptyState ? (
          <NoData icon={<Icons.Nut />} title="No resource metrics available" />
        ) : (
          <Box
            display="grid"
            gridTemplateColumns={`repeat(${panelCount}, minmax(${minResourceCardWidth}px, 1fr))`}
            gap={16}
            sx={mergeSx(
              panelCount === 4 && {
                '@container (max-width: 1098px)': {
                  gridTemplateColumns: `repeat(2, minmax(${minResourceCardWidth}px, 1fr))`,
                },
                '@container (max-width: 560px)': {
                  gridTemplateColumns: '1fr',
                },
              },
              panelCount === 3 && {
                '@container (max-width: 846px)': {
                  gridTemplateColumns: '1fr',
                },
              },
              panelCount === 2 && {
                '@container (max-width: 620px)': {
                  gridTemplateColumns: '1fr',
                },
              }
            )}
          >
            {isLoading
              ? Array.from({ length: panelCount }).map((_, index) => (
                  <ResourcePanelSkeleton key={index} />
                ))
              : null}
            {!isLoading && resources.cpu ? (
              <ResourcePanel resource={resources.cpu} />
            ) : null}
            {!isLoading && resources.memory ? (
              <ResourcePanel
                resource={resources.memory}
                locked={showAgentInstallBanner}
                onInstallAgent={openOnboardClusterDialog}
              />
            ) : null}
            {!isLoading && resources.gpu ? (
              <ResourcePanel
                resource={resources.gpu}
                locked={showAgentInstallBanner}
                onInstallAgent={openOnboardClusterDialog}
                headerEndContent={
                  <Button
                    testId="open-gpu-workloads-button"
                    variant="text"
                    size="sm"
                    component={RouterLink}
                    to={buildClusterInventoryWorkloadsGpuUtilizationPath(
                      cluster.id
                    )}
                    endIcon={<Icons.ArrowRight />}
                  >
                    View GPU workloads
                  </Button>
                }
              />
            ) : null}
            {!isLoading && resources.storage ? (
              <ResourcePanel
                resource={resources.storage}
                locked={showAgentInstallBanner}
                onInstallAgent={openOnboardClusterDialog}
              />
            ) : null}
          </Box>
        )}
      </DashboardSectionError>
    </LayoutCard>
  );
};
